import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation, Link } from "wouter";
import {
  Camera,
  ChevronLeft,
  Plus,
  X,
  Loader2,
  AlertCircle,
  BookOpen,
  Scan,
  RefreshCw,
  FileText,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { getListRecipesQueryKey } from "@workspace/api-client-react";

const MAX_FILES = 3;

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const LEGACY_DOC_MIME = "application/msword";

const LOADING_MESSAGES = [
  "Reading your recipe…",
  "Extracting ingredients…",
  "Almost done…",
];

type CapturedKind = "image" | "docx";

interface CapturedFile {
  file: File;
  kind: CapturedKind;
  previewUrl: string | null;
  uploadedId?: number;
}

function classifyFile(file: File): CapturedKind | "unsupported-doc" | "unsupported" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type === DOCX_MIME || /\.docx$/i.test(file.name)) return "docx";
  if (file.type === LEGACY_DOC_MIME || /\.doc$/i.test(file.name)) {
    return "unsupported-doc";
  }
  return "unsupported";
}

interface JobPollResult {
  state: "created" | "retry" | "active" | "completed" | "cancelled" | "failed";
  retryCount: number;
  recipesExtracted: number;
  recipeIds: number[];
  error?: string;
}

/**
 * Poll the queued extraction job until it reaches a terminal state.
 *
 * Resolves with the recipe ids on success; throws on a final failure
 * (after pg-boss exhausts retries) or on a hard timeout. The 6-minute cap
 * comfortably covers Claude's worst-case extraction plus one retry —
 * if we hit the cap, the worker likely crashed and the user should retry
 * from the Dashboard rather than wait forever in the scan flow.
 *
 * Calls `onSlow()` once after `SLOW_THRESHOLD_MS` if the job hasn't
 * reached a terminal state yet. The caller uses that signal to switch
 * the UI from a plain spinner to a "still working — this is unusual"
 * panel with retry/cancel affordances. We keep polling after onSlow
 * fires so a late completion still flows through to the success path.
 *
 * `signal` lets the caller abort the poll loop (e.g. user hit Cancel
 * or Retry on the slow panel). Aborting throws an `AbortError` which
 * the caller is expected to ignore in those flows.
 */
async function pollExtractionJob(
  jobId: string,
  signal: AbortSignal,
  onSlow: () => void,
): Promise<number[]> {
  const POLL_INTERVAL_MS = 1500;
  const SLOW_THRESHOLD_MS = 90_000;
  const MAX_DURATION_MS = 6 * 60 * 1000;
  const start = Date.now();
  let firedSlow = false;

  while (Date.now() - start < MAX_DURATION_MS) {
    if (signal.aborted) {
      throw new DOMException("Polling aborted", "AbortError");
    }
    if (!firedSlow && Date.now() - start > SLOW_THRESHOLD_MS) {
      firedSlow = true;
      onSlow();
    }
    const r = await fetch(
      `/api/extract/job/${encodeURIComponent(jobId)}`,
      { signal },
    );
    if (r.status === 404) {
      // pg-boss prunes completed jobs eventually; if we somehow miss
      // the completion window, treat it as an unknown failure.
      throw new Error("Extraction job not found");
    }
    if (!r.ok) {
      throw new Error("Couldn't read extraction status");
    }
    const job = (await r.json()) as JobPollResult;
    if (job.state === "completed") {
      return job.recipeIds;
    }
    if (job.state === "failed" || job.state === "cancelled") {
      throw new Error(job.error ?? "Extraction failed");
    }
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      }, POLL_INTERVAL_MS);
      const onAbort = () => {
        clearTimeout(timer);
        signal.removeEventListener("abort", onAbort);
        reject(new DOMException("Polling aborted", "AbortError"));
      };
      signal.addEventListener("abort", onAbort);
    });
  }
  throw new Error(
    "Extraction is taking longer than expected — check the dashboard in a minute.",
  );
}

interface ExtractedRecipeCard {
  id: number;
  title: string;
}

type PageState =
  | "capture"
  | "loading"
  | "slow"
  | "multi-result"
  | "error";

export default function ScanRecipePage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [pageState, setPageState] = useState<PageState>("capture");
  const [files, setFiles] = useState<CapturedFile[]>([]);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [multiRecipes, setMultiRecipes] = useState<ExtractedRecipeCard[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMoreRef = useRef<HTMLInputElement>(null);
  const loadingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks the in-flight extraction poll so Retry/Cancel from the
  // "slow" panel can abort the prior request before kicking off a
  // fresh one. The server-side job is left alone — the runtime
  // sweeper will reap it if the worker truly died.
  const pollAbortRef = useRef<AbortController | null>(null);

  // Rotate loading messages
  useEffect(() => {
    if (pageState === "loading") {
      setLoadingMsgIdx(0);
      loadingTimerRef.current = setInterval(() => {
        setLoadingMsgIdx((i) => Math.min(i + 1, LOADING_MESSAGES.length - 1));
      }, 2200);
    } else {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
    return () => {
      if (loadingTimerRef.current) clearInterval(loadingTimerRef.current);
    };
  }, [pageState]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  const handleFilesSelected = useCallback((selected: FileList | null) => {
    if (!selected || selected.length === 0) return;
    const incoming = Array.from(selected).slice(0, MAX_FILES - files.length);
    const newCaptures: CapturedFile[] = [];
    let rejectedDoc = false;
    let rejectedOther = false;

    for (const file of incoming) {
      const kind = classifyFile(file);
      if (kind === "image") {
        newCaptures.push({
          file,
          kind: "image",
          previewUrl: URL.createObjectURL(file),
        });
      } else if (kind === "docx") {
        newCaptures.push({ file, kind: "docx", previewUrl: null });
      } else if (kind === "unsupported-doc") {
        rejectedDoc = true;
      } else {
        rejectedOther = true;
      }
    }

    if (rejectedDoc) {
      toast({
        title: "Legacy .doc files aren't supported",
        description: "Please save as .docx and try again.",
        variant: "destructive",
      });
    } else if (rejectedOther) {
      toast({
        title: "Unsupported file type",
        description: "Please choose a photo or a Word (.docx) document.",
        variant: "destructive",
      });
    }

    if (newCaptures.length > 0) {
      setFiles((prev) => [...prev, ...newCaptures]);
    }
  }, [files, toast]);

  const removeFile = useCallback((idx: number) => {
    setFiles((prev) => {
      const removed = prev[idx];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const handleExtract = useCallback(async () => {
    if (files.length === 0) return;
    // Abort any prior in-flight poll (e.g. user hit Retry on the slow
    // panel) before starting fresh.
    pollAbortRef.current?.abort();
    const abortCtl = new AbortController();
    pollAbortRef.current = abortCtl;
    setPageState("loading");
    setErrorMsg(null);

    const imageFiles = files.filter((f) => f.kind === "image");
    const docFiles = files.filter((f) => f.kind === "docx");

    try {
      const recipeIds: number[] = [];

      // ─── Image path: upload (+ group if multiple) → extract ────────────────
      if (imageFiles.length > 0) {
        const formData = new FormData();
        imageFiles.forEach((f) => formData.append("images", f.file));

        const uploadRes = await fetch("/api/images/upload", {
          method: "POST",
          body: formData,
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? "Upload failed");
        }
        const uploadData = await uploadRes.json() as {
          uploaded: { id: number; filename: string }[];
          batchId: string;
        };
        const uploadedIds = uploadData.uploaded.map((u) => u.id);

        if (uploadedIds.length > 1) {
          const groupRes = await fetch("/api/images/group", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageIds: uploadedIds }),
          });
          if (!groupRes.ok) {
            const err = await groupRes.json().catch(() => ({}));
            throw new Error((err as { error?: string }).error ?? "Group failed");
          }
        }

        // Enqueue the extraction job. Server returns immediately with a
        // jobId; the actual Claude call runs in the background worker.
        const extractRes = await fetch("/api/extract", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageId: uploadedIds[0] }),
        });
        if (!extractRes.ok) {
          const err = await extractRes.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error ?? "Extraction failed");
        }
        const enqueued = (await extractRes.json()) as {
          success: boolean;
          jobId?: string;
          // `queued: true` = we just created a new job. `queued: false`
          // = an in-flight job already existed for this image (e.g. the
          // user double-tapped Scan), and the server returned that
          // existing job's id so we can poll it the same way.
          queued?: boolean;
          error?: string;
        };
        if (!enqueued.success || !enqueued.jobId) {
          throw new Error(enqueued.error ?? "Couldn't queue extraction");
        }

        // Poll the job until it completes (or hits a terminal failure
        // state). Polling cadence is intentionally modest — the worker
        // typically finishes in under a minute, but Claude can be slow,
        // so we cap total wait at ~6 minutes. After 90s of polling we
        // switch the UI to a "still working" panel with retry/cancel
        // affordances so the user isn't staring at an indefinite spinner.
        const newRecipeIds = await pollExtractionJob(
          enqueued.jobId,
          abortCtl.signal,
          () => {
            // Only flip to slow if we're still in loading — the user
            // may have already aborted via Retry, in which case
            // pageState will have been reset by the new handleExtract.
            setPageState((cur) => (cur === "loading" ? "slow" : cur));
          },
        );
        if (newRecipeIds.length === 0) {
          throw new Error("Couldn't find any recipes in the image");
        }
        recipeIds.push(...newRecipeIds);
      }

      // ─── Document path: one upload-extract call per .docx ──────────────────
      for (const doc of docFiles) {
        const formData = new FormData();
        formData.append("document", doc.file);
        const docRes = await fetch("/api/documents/upload-extract", {
          method: "POST",
          body: formData,
        });
        const docData = await docRes.json().catch(() => ({})) as {
          success?: boolean;
          recipesExtracted?: number;
          recipeIds?: number[];
          error?: string;
        };
        if (!docRes.ok || !docData.success || !docData.recipeIds || docData.recipeIds.length === 0) {
          throw new Error(
            docData.error ?? `Couldn't find any recipes in ${doc.file.name}`
          );
        }
        recipeIds.push(...docData.recipeIds);
      }

      if (recipeIds.length === 0) {
        throw new Error("Couldn't find any recipes");
      }

      queryClient.invalidateQueries({ queryKey: getListRecipesQueryKey() });

      if (recipeIds.length === 1) {
        // Single recipe — go straight to editor
        toast({ title: "Recipe extracted! Review it below." });
        navigate(`/recipe/${recipeIds[0]}/edit?from=scan`);
        return;
      }

      // Multiple recipes — fetch titles for interstitial
      const titleCards: ExtractedRecipeCard[] = await Promise.all(
        recipeIds.map(async (id) => {
          try {
            const r = await fetch(`/api/recipes/${id}`);
            const d = await r.json() as { title?: string };
            return { id, title: d.title ?? `Recipe #${id}` };
          } catch {
            return { id, title: `Recipe #${id}` };
          }
        })
      );

      setMultiRecipes(titleCards);
      setPageState("multi-result");
    } catch (err) {
      // AbortError is expected when the user hit Retry/Cancel on the
      // slow panel — a new handleExtract or handleReset is already in
      // motion, so we deliberately swallow it instead of routing to
      // the error screen.
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      const msg = err instanceof Error ? err.message : "Something went wrong";
      setErrorMsg(msg);
      setPageState("error");
    }
  }, [files, navigate, queryClient, toast]);

  const handleReset = useCallback(() => {
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    files.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setFiles([]);
    setMultiRecipes([]);
    setErrorMsg(null);
    setPageState("capture");
  }, [files]);

  // Abort any in-flight poll if the user navigates away mid-extract.
  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
      pollAbortRef.current = null;
    };
  }, []);

  // ── Loading overlay ────────────────────────────────────────────────────────
  if (pageState === "loading") {
    return (
      <Layout>
        <div className="flex-1 flex flex-col items-center justify-center min-h-0 bg-background">
          <div className="relative w-full max-w-sm mx-auto px-4">
            {/* File preview stack */}
            {files.length > 0 && (
              <div className="relative mb-8">
                {files.slice().reverse().map((f, stackIdx) => {
                  const commonStyle: React.CSSProperties = {
                    position: stackIdx === 0 ? "relative" : "absolute",
                    top: stackIdx === 0 ? 0 : `${stackIdx * -4}px`,
                    left: stackIdx === 0 ? 0 : `${stackIdx * 4}px`,
                    zIndex: files.length - stackIdx,
                    opacity: stackIdx === 0 ? 1 : 0.6 - stackIdx * 0.15,
                    maxHeight: "320px",
                  };
                  if (f.kind === "image" && f.previewUrl) {
                    return (
                      <img
                        key={f.previewUrl}
                        src={f.previewUrl}
                        alt="Scanning"
                        className="w-full rounded-xl object-cover shadow-md"
                        style={commonStyle}
                      />
                    );
                  }
                  return (
                    <div
                      key={f.file.name + stackIdx}
                      className="w-full aspect-[4/3] rounded-xl shadow-md bg-muted flex flex-col items-center justify-center gap-3 px-4 text-center"
                      style={commonStyle}
                    >
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <FileText className="w-7 h-7 text-primary" />
                      </div>
                      <p className="text-sm font-semibold text-foreground line-clamp-2 break-all">
                        {f.file.name}
                      </p>
                    </div>
                  );
                })}
                {/* Shimmer overlay */}
                <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    style={{
                      animation: "shimmer 1.8s infinite",
                      backgroundSize: "200% 100%",
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-primary animate-spin" />
              </div>
              <div>
                <p
                  className="text-lg font-semibold text-foreground transition-all duration-500"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {LOADING_MESSAGES[loadingMsgIdx]}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  This usually takes 10–20 seconds
                </p>
              </div>
            </div>
          </div>
          <style>{`
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
          `}</style>
        </div>
      </Layout>
    );
  }

  // ── Slow / "still working" panel ───────────────────────────────────────────
  // Fires after ~90s of polling without a terminal job state. We keep
  // polling in the background — if the worker comes through, the
  // success path still fires and the user is whisked to the editor.
  // Otherwise, Try Again re-uploads + re-extracts (fresh image ids,
  // fresh job; the runtime sweeper handles the orphan), and Cancel
  // returns to capture.
  if (pageState === "slow") {
    return (
      <Layout>
        <div className="max-w-sm mx-auto px-4 py-8 sm:py-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
            <Loader2 className="w-7 h-7 text-primary animate-spin" />
          </div>
          <h2
            className="text-xl font-bold text-foreground mb-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Still working — this is unusual
          </h2>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            Most recipes finish in under a minute. We're still trying in
            the background. You can keep waiting, try again with the same
            photo, or cancel and start over.
          </p>
          <div className="flex flex-col gap-3">
            <Button className="w-full gap-2" onClick={handleExtract}>
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleReset}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Multi-recipe interstitial ──────────────────────────────────────────────
  if (pageState === "multi-result") {
    const batchStr = multiRecipes.map((r) => r.id).join(",");
    return (
      <Layout>
        <div className="max-w-lg mx-auto px-4 py-8 sm:py-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
              <BookOpen className="w-7 h-7 text-primary" />
            </div>
            <h1
              className="text-2xl font-bold text-foreground"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {multiRecipes.length === 1
                ? "We found 1 recipe!"
                : `We found ${multiRecipes.length} recipes on this page!`}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Tap a card to review it, or review them all in sequence.
            </p>
          </div>

          {/* Recipe cards */}
          <div className="flex flex-col gap-3 mb-8">
            {multiRecipes.map((recipe, idx) => (
              <button
                key={recipe.id}
                className="w-full text-left border rounded-xl p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors group"
                onClick={() =>
                  navigate(
                    `/recipe/${recipe.id}/edit?from=scan&batch=${batchStr}`
                  )
                }
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    {idx + 1}
                  </span>
                  <span className="font-medium text-foreground line-clamp-2">
                    {recipe.title}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              className="w-full gap-2"
              onClick={() =>
                navigate(
                  `/recipe/${multiRecipes[0].id}/edit?from=scan&batch=${batchStr}`
                )
              }
            >
              <Scan className="w-4 h-4" />
              Review All ({multiRecipes.length})
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={handleReset}
            >
              Scan Another Recipe
            </Button>
            <Link
              href="/"
              className="text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Back to Cookbook
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (pageState === "error") {
    return (
      <Layout>
        <div className="max-w-sm mx-auto px-4 py-8 sm:py-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 mb-4">
            <AlertCircle className="w-7 h-7 text-destructive" />
          </div>
          <h2
            className="text-xl font-bold text-foreground mb-2"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Couldn't read this recipe
          </h2>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            The image might be blurry or at an angle. Try again with a clearer
            photo, or type the recipe in manually.
          </p>
          <div className="flex flex-col gap-3">
            <Button className="w-full gap-2" onClick={handleReset}>
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/recipe/new")}
            >
              Type It Instead
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Capture state (default) ────────────────────────────────────────────────
  return (
    <Layout>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Top bar */}
        <div className="border-b bg-background px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back
          </Link>
          <h1
            className="text-base font-semibold"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Scan a Recipe
          </h1>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="max-w-lg mx-auto w-full px-4 py-6 flex flex-col gap-6">

            {/* Photo area */}
            {files.length === 0 ? (
              /* Empty — big tap-to-capture zone */
              <button
                className="w-full aspect-[3/4] max-h-[480px] rounded-2xl border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-4 hover:border-primary/50 hover:bg-primary/5 transition-colors group"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Camera className="w-8 h-8 text-primary" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-foreground text-sm">
                    Take a photo or choose a file
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Photos or Word (.docx) documents
                  </p>
                </div>
              </button>
            ) : (
              /* Thumbnails */
              <div className="flex flex-col gap-4">
                {/* Primary preview (first item large) */}
                <div className="relative rounded-xl overflow-hidden bg-muted aspect-[4/3]">
                  {files[0].kind === "image" && files[0].previewUrl ? (
                    <img
                      src={files[0].previewUrl}
                      alt="Page 1"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-4 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <FileText className="w-7 h-7 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground line-clamp-2 break-all">
                          {files[0].file.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Word document
                        </p>
                      </div>
                    </div>
                  )}
                  <button
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                    onClick={() => removeFile(0)}
                    title="Remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                    {files[0].kind === "image" ? "Page 1" : "Document"}
                  </div>
                </div>

                {/* Extra files */}
                {files.length > 1 && (
                  <div className="flex gap-3 flex-wrap">
                    {files.slice(1).map((f, i) => (
                      <div
                        key={(f.previewUrl ?? f.file.name) + i}
                        className="relative w-28 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0"
                      >
                        {f.kind === "image" && f.previewUrl ? (
                          <img
                            src={f.previewUrl}
                            alt={`Page ${i + 2}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-1 text-center">
                            <FileText className="w-5 h-5 text-primary" />
                            <span className="text-[10px] text-foreground line-clamp-2 break-all leading-tight">
                              {f.file.name}
                            </span>
                          </div>
                        )}
                        <button
                          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                          onClick={() => removeFile(i + 1)}
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">
                          {f.kind === "image" ? i + 2 : "doc"}
                        </div>
                      </div>
                    ))}

                    {/* Add another file */}
                    {files.length < MAX_FILES && (
                      <button
                        className="w-28 h-20 rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center gap-1 hover:border-primary/50 hover:bg-primary/5 transition-colors flex-shrink-0"
                        onClick={() => addMoreRef.current?.click()}
                      >
                        <Plus className="w-5 h-5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Add file</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Add another file (when only 1 item and under limit) */}
                {files.length === 1 && files.length < MAX_FILES && (
                  <button
                    className="w-full py-3 rounded-xl border border-dashed border-muted-foreground/30 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground hover:bg-primary/5 transition-colors"
                    onClick={() => addMoreRef.current?.click()}
                  >
                    <Plus className="w-4 h-4" />
                    Add Another File
                  </button>
                )}
              </div>
            )}

            {/* File count hint */}
            {files.length > 0 && (
              <p className="text-xs text-muted-foreground text-center">
                {files.length} file{files.length !== 1 ? "s" : ""} selected
                {files.length < MAX_FILES ? ` · up to ${MAX_FILES - files.length} more` : ""}
              </p>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-3">
              <Button
                size="lg"
                className="w-full gap-2"
                disabled={files.length === 0}
                onClick={handleExtract}
              >
                <Scan className="w-4 h-4" />
                Extract Recipe
              </Button>

              {files.length > 0 && (
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={handleReset}
                >
                  Start Over
                </Button>
              )}
              <Link
                href="/"
                className="text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </Link>
            </div>

          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFilesSelected(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={addMoreRef}
          type="file"
          accept="image/*,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          className="hidden"
          onChange={(e) => {
            handleFilesSelected(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
    </Layout>
  );
}
